import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Ribbon, Layers, Ruler, Syringe, Activity, Loader2, Beaker } from "lucide-react";
import { ACCENT } from "@/components/oncology/shared";
import OverviewTab from "@/components/oncology/OverviewTab";
import TnmTab from "@/components/oncology/TnmTab";
import RecistTab from "@/components/oncology/RecistTab";
import ChemoTab from "@/components/oncology/ChemoTab";
import PerfTab from "@/components/oncology/PerfTab";
import ToxicityTab from "@/components/oncology/ToxicityTab";

/* ------------------------------------------------------------------ */
/* Oncology dashboard — 5 tabs                                        */
/*   1. Overview           — latest diagnosis + RECIST + chemo + ECOG */
/*   2. TNM Staging        — record dx, live-preview stage grouping   */
/*   3. RECIST Tracker     — measurement log + SLD trend + response   */
/*   4. Chemo Planner      — cycle log + regimen progress + toxicity  */
/*   5. Performance Status — ECOG/Karnofsky trend                     */
/* ------------------------------------------------------------------ */

export default function Oncology() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTabState] = useState(searchParams.get("tab") || "overview");
  const setTab = (t) => {
    setTabState(t);
    const next = new URLSearchParams(searchParams);
    next.set("tab", t);
    setSearchParams(next, { replace: true });
  };
  useEffect(() => {
    const urlTab = searchParams.get("tab");
    if (urlTab && urlTab !== tab) setTabState(urlTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const [dx, setDx] = useState({ diagnoses: [] });
  const [meas, setMeas] = useState({ measurements: [], summary: {} });
  const [chemo, setChemo] = useState({ cycles: [], summary: {} });
  const [perf, setPerf] = useState({ records: [] });
  const [tox, setTox] = useState({ toxicities: [], summary: {} });
  const [ref, setRef] = useState({ cancer_types: [], t_values: [], n_values: [], m_values: [], ecog_scale: [], regimens: [], toxicities: [] });
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [d, m, c, p, r, t] = await Promise.all([
        api.get("/specialties/oncology/diagnosis"),
        api.get("/specialties/oncology/measurement"),
        api.get("/specialties/oncology/chemo"),
        api.get("/specialties/oncology/performance"),
        api.get("/specialties/oncology/reference"),
        api.get("/specialties/oncology/toxicities"),
      ]);
      setDx(d.data); setMeas(m.data); setChemo(c.data); setPerf(p.data); setRef(r.data); setTox(t.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load oncology data");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const TABS = [
    { key: "overview",    label: "Overview",           icon: Ribbon },
    { key: "tnm",         label: "TNM Staging",        icon: Layers },
    { key: "recist",      label: "RECIST",             icon: Ruler },
    { key: "chemo",       label: "Chemo Planner",      icon: Syringe },
    { key: "toxicity",    label: "CTCAE Toxicity",     icon: Beaker },
    { key: "performance", label: "Performance",        icon: Activity },
  ];

  return (
    <div className="space-y-6" data-testid="oncology-page">
      <div className="ros-card">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center"
               style={{ background: `${ACCENT}22`, border: `1px solid ${ACCENT}55` }}>
            <Ribbon size={22} style={{ color: ACCENT }} />
          </div>
          <div>
            <h2 className="font-heading text-2xl">Oncology console</h2>
            <p className="text-sm text-[#A3ACA7] mt-0.5">
              AJCC 8th TNM staging, RECIST 1.1 response, chemo cycle planning, ECOG/Karnofsky performance.
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="onco-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
                    data-testid={`onco-tab-${t.key}`}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                      active ? "text-black border-transparent"
                             : "bg-[#0F1312] text-[#A3ACA7] border-[#272E2B] hover:text-white hover:border-[#FBBF24]/40"
                    }`}
                    style={active ? { background: ACCENT } : {}}>
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-center text-[#A3ACA7] py-16 flex items-center justify-center gap-2">
          <Loader2 size={16} className="animate-spin" /> Loading oncology data…
        </div>
      ) : (
        <>
          {tab === "overview"    && <OverviewTab dx={dx} meas={meas} chemo={chemo} perf={perf} onJump={setTab} />}
          {tab === "tnm"         && <TnmTab      data={dx}    ref_={ref} onSaved={loadAll} />}
          {tab === "recist"      && <RecistTab   data={meas}             onSaved={loadAll} />}
          {tab === "chemo"       && <ChemoTab    data={chemo} ref_={ref} onSaved={loadAll} />}
          {tab === "toxicity"    && <ToxicityTab data={tox}              onSaved={loadAll} />}
          {tab === "performance" && <PerfTab     data={perf}             onSaved={loadAll} />}
        </>
      )}
    </div>
  );
}
